import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type {
  WikiCollection,
  WikiCollectionInput,
  WikiCollectionMember,
  WikiCollectionMemberInput,
  WikiPage,
  WikiPageInput,
  WikiPageMember,
  WikiPageMemberInput,
  WikiSticky,
  WikiStickyInput,
} from './types';

const toastErr = (err: ApiError) => {
  if (!err.fieldErrors) toast.error(err.message);
};

// ---------------------------------------------------------------------------
// Generic collection hooks (list/create/update/remove) — mirrors the projects
// feature's `makeCollectionHooks`. Invalidates the whole scope on any write.
// ---------------------------------------------------------------------------
function makeCollectionHooks<T, TInput>(
  scope: string,
  resource: {
    list: (p?: Record<string, unknown>) => Promise<{ results: T[] } | T[]>;
    create: (i: TInput) => Promise<T>;
    update: (id: string | number, i: Partial<TInput>) => Promise<T>;
    remove: (id: string | number) => Promise<void>;
  },
  toasts: { created?: string; removed?: string } = {},
) {
  const keys = {
    all: [scope] as const,
    list: (p: Record<string, unknown>) => [scope, 'list', p] as const,
  };
  return {
    keys,
    useList: (params: Record<string, unknown> = {}, enabled = true) =>
      useQuery({
        queryKey: keys.list(params),
        queryFn: () => resource.list(params),
        placeholderData: keepPreviousData,
        enabled,
      }),
    useCreate: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (input: TInput) => resource.create(input),
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: keys.all });
          if (toasts.created) toast.success(toasts.created);
        },
        onError: toastErr,
      });
    },
    useUpdate: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: ({ id, input }: { id: string | number; input: Partial<TInput> }) =>
          resource.update(id, input),
        onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
        onError: toastErr,
      });
    },
    useRemove: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (id: string | number) => resource.remove(id),
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: keys.all });
          if (toasts.removed) toast.success(toasts.removed);
        },
        onError: toastErr,
      });
    },
  };
}

export const collectionsHooks = makeCollectionHooks<WikiCollection, WikiCollectionInput>(
  'wiki-collections',
  api.collectionsApi,
  { created: 'Collection created', removed: 'Collection deleted' },
);

export const collectionMembersHooks = makeCollectionHooks<
  WikiCollectionMember,
  WikiCollectionMemberInput
>('wiki-collection-members', api.collectionMembersApi);

export const pagesHooks = makeCollectionHooks<WikiPage, WikiPageInput>('wiki-pages', api.pagesApi);

export const pageMembersHooks = makeCollectionHooks<WikiPageMember, WikiPageMemberInput>(
  'wiki-page-members',
  api.pageMembersApi,
  { created: 'Shared', removed: 'Access removed' },
);

export const stickiesHooks = makeCollectionHooks<WikiSticky, WikiStickyInput>(
  'wiki-stickies',
  api.stickiesApi,
);

// ---- Collection archive / restore ----
export function useCollectionAction(fn: (id: string) => Promise<unknown>, success?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionsHooks.keys.all });
      if (success) toast.success(success);
    },
    onError: toastErr,
  });
}

export function useReorderCollections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.collectionsApi.reorder(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: collectionsHooks.keys.all }),
    onError: toastErr,
  });
}

// ---- Page versions / actions ----
export function usePageVersions(id: string | number | null) {
  return useQuery({
    queryKey: ['wiki-pages', 'versions', id],
    queryFn: () => api.pagesApi.versions(id as string | number),
    enabled: id != null,
  });
}

/** Page actions (publish/archive/restore/restore-version) — refresh the pages scope. */
export function usePageAction<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wiki-pages'] }),
    onError: toastErr,
  });
}

/** Queue a page into the Knowledge Base RAG index — mirrors usePageAction but
 *  doesn't touch the pages scope (nothing about the page itself changes). */
export function useSyncPageToRag() {
  return useMutation({
    mutationFn: (id: number) => api.pagesApi.syncToRag(id),
    onSuccess: () => toast.success('Queued for the knowledge base'),
    onError: toastErr,
  });
}

/** Load a single page by public_id (focused view from Recents/Pinned). */
export function useWikiPage(publicId: string | null) {
  return useQuery({
    queryKey: ['wiki-pages', 'detail', publicId],
    queryFn: () => api.pagesApi.get(publicId as string),
    enabled: publicId != null,
  });
}

// ---- Home (recents + stickies) ----
export function useWikiHome() {
  return useQuery({
    queryKey: ['wiki-home'],
    queryFn: () => api.getWikiHome(),
  });
}

/** Record an open (Recents) — fire-and-forget, refreshes Home. */
export function useRecordVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.pagesApi.visit(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wiki-home'] }),
  });
}
